<template>
  <header class="shadow flex items-center dark:bg-gray-900 dark:text-gray-100 py-4 bg-white">
    <div class="px-4 flex items-center">
      <kt-button icon-only icon="ki-duotone ki-burger-menu-6" class="lg:hidden" @click="toggleSidebar" />
    </div>

    <div class="flex-grow"></div>

    <div class="px-4 flex flex-row gap-4 items-center relative">
      <div class="relative inline-block" ref="notifDropdownRef">
        <button
          @click="toggleNotifDropdown"
          class="relative flex items-center justify-center w-10 h-10 rounded-full group transition-colors"
        >
          <kt-icon name="notification" type="duotone" class="text-2xl group-hover:text-primary" />
          <span
            v-if="notif_counts.total_notifications > 0"
            class="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full border-2 border-white dark:border-gray-900"
          >
            {{ notif_counts.total_notifications }}
          </span>
        </button>

        <transition name="fade-slide">
          <kt-card
            v-if="isNotifDropdownOpen"
            class="absolute top-full right-0 mt-3 w-80 md:w-96 max-h-[500px] z-50 shadow-xl border border-gray-100 dark:border-gray-800"
          >
            <kt-card-header class="border-b">
              <h1 class="text-lg font-bold">Notifications</h1>
            </kt-card-header>
            <kt-card-body v-if="loading" class="p-10 flex justify-center">
              <span class="loading-spinner"></span>
            </kt-card-body>
            <kt-card-body v-else class="overflow-y-auto p-2 space-y-1">
              <div v-if="notifications.length === 0" class="py-10 flex flex-col items-center opacity-60">
                <img src="/public/media/empty-state/no-data.svg" alt="No Notifications" class="w-24 mb-4" />
                <p class="font-medium">All caught up!</p>
              </div>
              <div
                v-else
                v-for="value in notifications"
                :key="value.id"
                @click="goToNotif(value)"
                class="hover:bg-gray-100 dark:hover:bg-gray-800 p-3 rounded-xl cursor-pointer transition-all"
              >
                <h1 class="text-sm font-semibold">{{ value.title }}</h1>
                <p class="text-xs text-gray-500 mt-1">{{ value.message }}</p>
              </div>
            </kt-card-body>
          </kt-card>
        </transition>
      </div>

      <div class="relative" ref="dropdownRef">
        <div @click="toggleDropdown" class="flex items-center gap-4 cursor-pointer hover:opacity-80 transition-opacity">
          <kt-avatar :rounded-full="true" size="40" type="primary" class="shadow-sm">
            <img v-if="user?.avatar" :src="user.avatar" alt="User Profile" />
            <span v-else class="text-sm font-bold text-white">
              {{ user?.name ? user.name.charAt(0).toUpperCase() : "U" }}
            </span>
          </kt-avatar>
        </div>

        <transition name="fade-slide">
          <kt-card
            v-if="isDropdownOpen"
            class="absolute top-full right-0 mt-3 w-64 z-50 shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden"
          >
            <div class="p-4 bg-gray-50 dark:bg-gray-800/50 border-b dark:border-gray-700">
              <p class="text-sm font-bold truncate">{{ user?.name || "User Name" }}</p>
              <p class="text-xs text-gray-500 truncate">{{ user?.email || "user@example.com" }}</p>
            </div>

            <div class="p-2 space-y-1">
              <button
                @click="handleMenuAction('profile')"
                class="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors"
              >
                <kt-icon name="user" class="text-lg" />
                My Profile
              </button>

              <div class="border-t dark:border-gray-700 my-1"></div>

              <button
                @click="handleMenuAction('logout')"
                class="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              >
                <kt-icon name="exit-right" class="text-lg" />
                Sign Out
              </button>
            </div>
          </kt-card>
        </transition>
      </div>
    </div>
  </header>
</template>

<style scoped>
/* Modern transition for the dropdowns */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.2s ease-out;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useNotificationStore } from "@/stores/notifications";
import useAction from "@/composables/useAction";
import { useRouter, useRoute } from "vue-router";
import { useDialog } from "metronics-vue";

const router = useRouter();
const route = useRoute();
const notif_store = useNotificationStore();
const authStore = useAuthStore();
const { showCustomConfirmDialog } = useDialog();

const notifications = computed(() => notif_store.notifications);
const notif_counts = computed(() => notif_store.count);
const user = authStore.user;

const isDropdownOpen = ref(false);
const isNotifDropdownOpen = ref(false);
const dropdownRef = ref(null);
const notifDropdownRef = ref(null);

const emit = defineEmits(["click:sidebar-toggler"]);

// Logic updated to handle profile navigation properly
function handleMenuAction(action) {
  isDropdownOpen.value = false;
  if (action === "profile") {
    router.push(`/user-management/agent-profile/${user.id}`);
  } else if (action === "logout") {
    handleLogout();
  }
}

const toggleDropdown = () => {
  isNotifDropdownOpen.value = false;
  isDropdownOpen.value = !isDropdownOpen.value;
};

const toggleNotifDropdown = () => {
  isDropdownOpen.value = false;
  isNotifDropdownOpen.value = !isNotifDropdownOpen.value;
};

const toggleSidebar = () => emit("click:sidebar-toggler");

const { invoke: fetch_notif, loading } = useAction(notif_store.fetch_all_notifications);
const { invoke: count_notif } = useAction(notif_store.count_all_notif);

const goToNotif = (notif) => {
  if (!notif.route) return;
  let targetRoute = notif.route.startsWith("/") ? notif.route : `/${notif.route}`;
  if (route.fullPath !== targetRoute) router.push(targetRoute);
  isNotifDropdownOpen.value = false;
};

const handleClickOutside = (e) => {
  if (
    dropdownRef.value &&
    !dropdownRef.value.contains(e.target) &&
    notifDropdownRef.value &&
    !notifDropdownRef.value.contains(e.target)
  ) {
    isDropdownOpen.value = false;
    isNotifDropdownOpen.value = false;
  }
};

async function handleLogout() {
  const confirm = await showCustomConfirmDialog({
    title: "Confirm Logout",
    subtitle: "Are you sure you want to log out? You will redirected to the EES login page."
  });

  if (!confirm) return;
  
  try {
    await authStore.logout();
  } catch (error) {
    console.error("Logout failed:", error);
  }
}

onMounted(() => {
  fetch_notif();
  count_notif();
  notif_store.startPolling(10000);
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  notif_store.stopPolling();
  document.removeEventListener("click", handleClickOutside);
});
</script>
